import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { 
  FaEye, 
  FaTrashAlt, 
  FaTimes, 
  FaSearch, 
  FaChevronDown, 
  FaChevronUp, 
  FaFileExport,
  FaBuilding,
  FaUserTie,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaAngleDoubleRight,
  FaCheck,
  FaBan,
  FaComments,
  FaClock
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import './InstitutionalEnquries.css';
import axiosInstance from '../../api/axiosInstance';
import Pagination from '../Pagination/Pagination';

/* =========================================================
   1. VIEW DETAIL MODAL
========================================================= */
const EnquiryViewModal = ({ enquiry, onClose }) => {
  if (!enquiry) return null;
  const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'N/A';

  return (
    <div className="intitutional-enqury-form-admin-page-modal-overlay" onClick={onClose}>
      <div className="intitutional-enqury-form-admin-page-modal-content" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="intitutional-enqury-form-admin-page-modal-header">
          <div className="intitutional-enqury-form-admin-page-modal-header-left">
            <span className="intitutional-enqury-form-admin-page-modal-kicker">Institutional Enquiry</span>
            <h2>{enquiry.institutionName || 'N/A'}</h2>
          </div>
          <div className="intitutional-enqury-form-admin-page-modal-header-right">
            <span className={`intitutional-enqury-form-admin-page-status-badge ${(enquiry.status || 'open').toLowerCase().replace(/\s+/g, '-')}`}>
              {enquiry.status || 'Open'}
            </span>
            <button className="intitutional-enqury-form-admin-page-modal-close-btn" onClick={onClose} aria-label="Close">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="intitutional-enqury-form-admin-page-modal-body">
          <div className="intitutional-enqury-form-admin-page-modal-hero-strip">
            <span className="intitutional-enqury-form-admin-page-strip-label">Service Required:</span>
            <strong>{enquiry.serviceRequired || 'N/A'}</strong>
          </div>

          <div className="intitutional-enqury-form-admin-page-modal-grid">
            <div className="intitutional-enqury-form-admin-page-modal-card">
              <div className="intitutional-enqury-form-admin-page-card-icon"><FaUserTie /></div>
              <div className="intitutional-enqury-form-admin-page-card-info">
                <label>Contact Person (Name)</label>
                <span>{enquiry.contactPerson || 'N/A'}</span>
              </div>
            </div>

            <div className="intitutional-enqury-form-admin-page-modal-card">
              <div className="intitutional-enqury-form-admin-page-card-icon"><FaBuilding /></div>
              <div className="intitutional-enqury-form-admin-page-card-info">
                <label>Designation</label>
                <span>{enquiry.designation || 'N/A'}</span>
              </div>
            </div>

            <div className="intitutional-enqury-form-admin-page-modal-card">
              <div className="intitutional-enqury-form-admin-page-card-icon"><FaPhoneAlt /></div>
              <div className="intitutional-enqury-form-admin-page-card-info">
                <label>Mobile / WhatsApp</label>
                <span>
                  {enquiry.mobileNumber || 'N/A'} 
                  {enquiry.whatsappNumber && enquiry.whatsappNumber !== 'N/A' && ` (WA: ${enquiry.whatsappNumber})`}
                </span>
              </div>
            </div>

            <div className="intitutional-enqury-form-admin-page-modal-card">
              <div className="intitutional-enqury-form-admin-page-card-icon"><FaEnvelope /></div>
              <div className="intitutional-enqury-form-admin-page-card-info">
                <label>Official Email</label>
                <span className="text-break">{enquiry.officialEmail || 'N/A'}</span>
              </div>
            </div>

            <div className="intitutional-enqury-form-admin-page-modal-card">
              <div className="intitutional-enqury-form-admin-page-card-icon"><FaMapMarkerAlt /></div>
              <div className="intitutional-enqury-form-admin-page-card-info">
                <label>Location</label>
                <span>{enquiry.city || 'N/A'}, {enquiry.state || 'N/A'}</span>
              </div>
            </div>

            <div className="intitutional-enqury-form-admin-page-modal-card">
              <div className="intitutional-enqury-form-admin-page-card-icon"><FaClock /></div>
              <div className="intitutional-enqury-form-admin-page-card-info">
                <label>Preferred Contact Time</label>
                <span>{enquiry.preferredContactTime || 'N/A'}</span>
              </div>
            </div>
          </div>

          <div className="intitutional-enqury-form-admin-page-modal-section">
            <label>Project Details / Requirements</label>
            <div className="intitutional-enqury-form-admin-page-modal-text-box">
              {enquiry.projectDetails || 'No additional project details provided.'}
            </div>
          </div>

          <div className="intitutional-enqury-form-admin-page-modal-footer-meta">
            <span>Submitted On: <strong>{formatDate(enquiry.createdAt)}</strong></span>
          </div>
        </div>

        <div className="intitutional-enqury-form-admin-page-modal-footer">
          <button type="button" className="intitutional-enqury-form-admin-page-modal-btn dismiss" onClick={onClose}>
            Close
          </button>
        </div>

      </div>
    </div>
  );
};

/* =========================================================
   2. PROCESS STATUS MODAL
========================================================= */
const ProcessStatusModal = ({ enquiry, onClose, onUpdateStatus, onDelete }) => {
  if (!enquiry) return null;

  return (
    <div className="intitutional-enqury-form-admin-page-process-modal-overlay" onClick={onClose}>
      <div className="intitutional-enqury-form-admin-page-process-modal-content" onClick={(e) => e.stopPropagation()}>
        
        <div className="intitutional-enqury-form-admin-page-process-modal-header">
          <div className="intitutional-enqury-form-admin-page-process-icon-badge">
            <FaAngleDoubleRight />
          </div>
          <div>
            <h3>Process Institutional Enquiry</h3>
            <p>Institution: <strong>{enquiry.institutionName || 'N/A'}</strong></p>
          </div>
          <button className="intitutional-enqury-form-admin-page-process-close-btn" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <div className="intitutional-enqury-form-admin-page-process-modal-body">
          <div className="intitutional-enqury-form-admin-page-process-status-indicator">
            <span>Current Status:</span>
            <span className={`intitutional-enqury-form-admin-page-status-badge ${(enquiry.status || 'open').toLowerCase().replace(/\s+/g, '-')}`}>
              {enquiry.status || 'Open'}
            </span>
          </div>

          <p className="intitutional-enqury-form-admin-page-process-instruction">
            Choose the next lifecycle stage for this project enquiry:
          </p>

          <div className="intitutional-enqury-form-admin-page-process-btn-stack">
            {enquiry.status === 'Reviewed' && (
              <>
                <button 
                  type="button" 
                  className="intitutional-enqury-form-admin-page-action-btn discussion"
                  onClick={() => { onClose(); onUpdateStatus(enquiry._id, 'In Discussion'); }}
                >
                  <FaComments /> Move to In Discussion
                </button>
                <button 
                  type="button" 
                  className="intitutional-enqury-form-admin-page-action-btn reject"
                  onClick={() => { onClose(); onUpdateStatus(enquiry._id, 'Rejected'); }}
                >
                  <FaBan /> Reject Enquiry
                </button>
              </>
            )}

            {enquiry.status === 'In Discussion' && (
              <>
                <button 
                  type="button" 
                  className="intitutional-enqury-form-admin-page-action-btn close-success"
                  onClick={() => { onClose(); onUpdateStatus(enquiry._id, 'Closed'); }}
                >
                  <FaCheck /> Mark Closed / Finalized
                </button>
                <button 
                  type="button" 
                  className="intitutional-enqury-form-admin-page-action-btn reject"
                  onClick={() => { onClose(); onUpdateStatus(enquiry._id, 'Rejected'); }}
                >
                  <FaBan /> Reject Enquiry
                </button>
              </>
            )}

            {enquiry.status === 'Closed' && (
              <>
                <button 
                  type="button" 
                  className="intitutional-enqury-form-admin-page-action-btn reject"
                  onClick={() => { onClose(); onUpdateStatus(enquiry._id, 'Rejected'); }}
                >
                  <FaBan /> Move to Rejected
                </button>
                <button 
                  type="button" 
                  className="intitutional-enqury-form-admin-page-action-btn delete"
                  onClick={() => { onClose(); onDelete(enquiry._id, enquiry.status); }}
                >
                  <FaTrashAlt /> Delete Record
                </button>
              </>
            )}

            {enquiry.status === 'Rejected' && (
              <>
                <button 
                  type="button" 
                  className="intitutional-enqury-form-admin-page-action-btn discussion"
                  onClick={() => { onClose(); onUpdateStatus(enquiry._id, 'In Discussion'); }}
                >
                  <FaComments /> Re-open to In Discussion
                </button>
                <button 
                  type="button" 
                  className="intitutional-enqury-form-admin-page-action-btn delete"
                  onClick={() => { onClose(); onDelete(enquiry._id, enquiry.status); }}
                >
                  <FaTrashAlt /> Delete Permanently
                </button>
              </>
            )}

            <button type="button" className="intitutional-enqury-form-admin-page-action-btn cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

const PAGE_OPTIONS = [10, 20, 30, 40, 50, 100];

/* =========================================================
   3. MAIN ADMIN PAGE
========================================================= */
const InstitutionalEnquries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [expandedRow, setExpandedRow] = useState(null);
  const [viewingEnquiry, setViewingEnquiry] = useState(null);
  const [processingEnquiry, setProcessingEnquiry] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchEnquiries = async () => {
    try {
      const res = await axiosInstance.get('/api/institution/all');
      setEnquiries(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      if (error.response?.status !== 401) console.error('Fetch enquiries error:', error);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const toggleRow = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'N/A');

  const filteredEnquiries = useMemo(() => {
    return enquiries.filter((item) => {
      if (statusFilter !== 'All' && item.status !== statusFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const mob = item.mobileNumber || '';
        const org = item.institutionName || '';
        const person = item.contactPerson || '';
        const srv = item.serviceRequired || '';
        const city = item.city || '';
        const state = item.state || '';
        return (
          org.toLowerCase().includes(q) ||
          person.toLowerCase().includes(q) ||
          item.officialEmail?.toLowerCase().includes(q) ||
          mob.includes(q) ||
          srv.toLowerCase().includes(q) ||
          city.toLowerCase().includes(q) ||
          state.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [enquiries, searchQuery, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const paginatedEnquiries = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredEnquiries.slice(start, start + itemsPerPage);
  }, [filteredEnquiries, currentPage, itemsPerPage]);

  const handleExportExcel = async () => {
    if (filteredEnquiries.length === 0) return Swal.fire('Notice', 'No data available to export.', 'info');

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Institutional Enquiries');

    worksheet.mergeCells('A1:K1');
    worksheet.getCell('A1').value = 'NUTRIVIO MEDICAL SERVICES PRIVATE LIMITED';
    worksheet.getCell('A1').font = { name: 'Arial', size: 15, bold: true, color: { argb: 'FF206E88' } };
    worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.mergeCells('A2:K2');
    worksheet.getCell('A2').value = `Institutional Project Enquiries Report (${statusFilter})`;
    worksheet.getCell('A2').font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FF475569' } };
    worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.addRow([]);

    const headerRow = worksheet.addRow([
      'S.No', 'Institution Name', 'Contact Person', 'Designation', 'Mobile', 'Official Email', 
      'City', 'State', 'Service Required', 'Status', 'Submitted On'
    ]);

    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF50A5C0' } };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    filteredEnquiries.forEach((item, i) => {
      const row = worksheet.addRow([
        i + 1,
        item.institutionName || 'N/A',
        item.contactPerson || 'N/A',
        item.designation || 'N/A',
        item.mobileNumber || 'N/A',
        item.officialEmail || 'N/A',
        item.city || 'N/A',
        item.state || 'N/A',
        item.serviceRequired || 'N/A',
        item.status || 'N/A',
        formatDate(item.createdAt)
      ]);

      row.eachCell((cell) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      });
      row.getCell(1).alignment = { horizontal: 'center' };

      const statusCell = row.getCell(10);
      statusCell.alignment = { horizontal: 'center' };
      statusCell.font = { 
        bold: true, 
        color: { 
          argb: item.status === 'Closed' ? 'FF16A34A' : 
                item.status === 'In Discussion' ? 'FF50A5C0' : 
                item.status === 'Reviewed' ? 'FF2563EB' : 
                item.status === 'Rejected' ? 'FFDC2626' : 'FFE67E22' 
        } 
      };
    });

    worksheet.columns = [
      { width: 8 }, { width: 28 }, { width: 22 }, { width: 20 }, { width: 16 }, { width: 28 }, 
      { width: 16 }, { width: 16 }, { width: 30 }, { width: 16 }, { width: 22 }
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Nutrivio_Institutional_Enquiries_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleView = async (item) => {
    if (item.status === 'Open') {
      try {
        await axiosInstance.put(`/api/institution/update/${item._id}`, { status: 'Reviewed' });
        item.status = 'Reviewed';
        fetchEnquiries();
      } catch (e) {
        console.error('Auto-review error:', e);
      }
    }
    setViewingEnquiry(item);
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await axiosInstance.put(`/api/institution/update/${id}`, { status: newStatus });
      fetchEnquiries();
      Swal.fire({
        icon: 'success',
        title: 'Status Updated',
        text: `Enquiry status marked as ${newStatus}.`,
        confirmButtonColor: '#50A5C0'
      });
    } catch (error) {
      Swal.fire('Error', 'Failed to update enquiry status.', 'error');
    }
  };

  const handleDelete = async (id, status) => {
    if (status !== 'Rejected' && status !== 'Closed') {
      return Swal.fire({
        icon: 'error',
        title: 'Action Denied',
        text: 'Only rejected or closed institutional enquiries can be deleted.',
        confirmButtonColor: '#50A5C0'
      });
    }

    const res = await Swal.fire({
      title: 'Delete Enquiry Record?',
      text: 'This institutional enquiry record will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      cancelButtonColor: '#64748B',
      confirmButtonText: 'Yes, Delete',
    });

    if (res.isConfirmed) {
      try {
        await axiosInstance.delete(`/api/institution/delete/${id}`);
        fetchEnquiries();
        Swal.fire('Deleted', 'Record removed successfully.', 'success');
      } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'Deletion failed.', 'error');
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Institutional Enquiries | Nutrivio Admin</title>
        <meta name="description" content="View and manage institutional project enquiries for Nutrivio Medical Services." />
      </Helmet>

      <div className="intitutional-enqury-form-admin-page-container">
        
        {/* Modals */}
        <EnquiryViewModal enquiry={viewingEnquiry} onClose={() => setViewingEnquiry(null)} />
        <ProcessStatusModal 
          enquiry={processingEnquiry} 
          onClose={() => setProcessingEnquiry(null)} 
          onUpdateStatus={handleUpdateStatus} 
          onDelete={handleDelete} 
        />

        <h2 className="intitutional-enqury-form-admin-page-title">Institutional Advisory &amp; Project Enquiries</h2>

        {/* Controls */}
        <div className="intitutional-enqury-form-admin-page-controls">
          <div className="intitutional-enqury-form-admin-page-search-wrapper">
            <FaSearch className="intitutional-enqury-form-admin-page-search-icon" />
            <input
              type="text"
              className="intitutional-enqury-form-admin-page-search-input"
              placeholder="Search Institution, Contact Person, City, Mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="intitutional-enqury-form-admin-page-action-group">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="intitutional-enqury-form-admin-page-select"
            >
              <option value="All">All Status</option>
              <option value="Open">Open</option>
              <option value="Reviewed">Reviewed</option>
              <option value="In Discussion">In Discussion</option>
              <option value="Closed">Closed</option>
              <option value="Rejected">Rejected</option>
            </select>

            <select
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="intitutional-enqury-form-admin-page-select"
            >
              {PAGE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt} / page</option>
              ))}
            </select>

            <button className="intitutional-enqury-form-admin-page-btn export" onClick={handleExportExcel} title="Export to Excel">
              <FaFileExport /> Export Excel
            </button>
          </div>
        </div>

        {/* Responsive Table */}
        <div className="intitutional-enqury-form-admin-page-table-wrapper">
          <table className="intitutional-enqury-form-admin-page-table">
            <thead>
              <tr>
                <th className="col-sno">S.No</th>

                {/* Visible on >= 1200px */}
                <th className="col-inst-person show-gt-1200 intitutional-enqury-form-admin-page-align-left">
                  Institution &amp; Name
                </th>
                <th className="col-contact show-gt-1200 intitutional-enqury-form-admin-page-align-left">
                  Contact &amp; Email
                </th>
                <th className="col-service show-gt-1200">
                  Service Required
                </th>

                {/* Visible on < 1200px */}
                <th className="col-inst hide-gt-1200 intitutional-enqury-form-admin-page-align-left">
                  Institution
                </th>
                <th className="col-name show-769-1199 intitutional-enqury-form-admin-page-align-left">
                  Name
                </th>
                <th className="col-phone show-992-1199 intitutional-enqury-form-admin-page-align-left">
                  Contact
                </th>

                {/* Visible on > 768px */}
                <th className="col-status show-gt-768">Status</th>

                {/* Always visible */}
                <th className="col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEnquiries.length === 0 ? (
                <tr>
                  <td colSpan="8" className="intitutional-enqury-form-admin-page-no-data">
                    No institutional enquiries found.
                  </td>
                </tr>
              ) : (
                paginatedEnquiries.map((item, i) => {
                  const globalIndex = (currentPage - 1) * itemsPerPage + i;
                  const isRowOpen = expandedRow === item._id;

                  return (
                    <React.Fragment key={item._id}>
                      <tr className={`intitutional-enqury-form-admin-page-main-row ${item.status === 'Open' ? 'unread' : ''} ${isRowOpen ? 'row-expanded' : ''}`}>
                        
                        {/* 1. S.No */}
                        <td className="col-sno">{globalIndex + 1}</td>

                        {/* >= 1200px: Combined Institution & Name */}
                        <td className="col-inst-person show-gt-1200 intitutional-enqury-form-admin-page-align-left">
                          <div className="intitutional-enqury-form-admin-page-name-wrapper" onClick={() => toggleRow(item._id)}>
                            <div className="intitutional-enqury-form-admin-page-inst-cluster">
                              <span className="intitutional-enqury-form-admin-page-inst-name">{item.institutionName || 'N/A'}</span>
                              <span className="intitutional-enqury-form-admin-page-person-sub">{item.contactPerson || 'N/A'}</span>
                            </div>
                            <button type="button" className="intitutional-enqury-form-admin-page-expand-btn" aria-label="Toggle Details">
                              {isRowOpen ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                          </div>
                        </td>

                        {/* >= 1200px: Combined Phone & Email */}
                        <td className="col-contact show-gt-1200 intitutional-enqury-form-admin-page-align-left">
                          <div className="intitutional-enqury-form-admin-page-contact-cluster">
                            <strong>{item.mobileNumber || 'N/A'}</strong>
                            <small>{item.officialEmail || 'N/A'}</small>
                          </div>
                        </td>

                        {/* >= 1200px: Service Required */}
                        <td className="col-service show-gt-1200">
                          <span className="intitutional-enqury-form-admin-page-service-pill">{item.serviceRequired || 'N/A'}</span>
                        </td>

                        {/* < 1200px: Institution Name only */}
                        <td className="col-inst hide-gt-1200 intitutional-enqury-form-admin-page-align-left">
                          <div className="intitutional-enqury-form-admin-page-name-wrapper" onClick={() => toggleRow(item._id)}>
                            <span className="intitutional-enqury-form-admin-page-inst-name">{item.institutionName || 'N/A'}</span>
                            <button type="button" className="intitutional-enqury-form-admin-page-expand-btn" aria-label="Toggle Details">
                              {isRowOpen ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                          </div>
                        </td>

                        {/* 769px - 1199px: Contact Person Name */}
                        <td className="col-name show-769-1199 intitutional-enqury-form-admin-page-align-left">
                          <span className="intitutional-enqury-form-admin-page-person-name">{item.contactPerson || 'N/A'}</span>
                        </td>

                        {/* 992px - 1199px: Mobile Contact */}
                        <td className="col-phone show-992-1199 intitutional-enqury-form-admin-page-align-left">
                          <strong>{item.mobileNumber || 'N/A'}</strong>
                        </td>

                        {/* > 768px: Status */}
                        <td className="col-status show-gt-768">
                          <span className={`intitutional-enqury-form-admin-page-status-badge ${(item.status || 'open').toLowerCase().replace(/\s+/g, '-')}`}>
                            {item.status || 'Open'}
                          </span>
                        </td>

                        {/* Always: Actions */}
                        <td className="col-action">
                          <div className="intitutional-enqury-form-admin-page-action-btns">
                            <button
                              className="intitutional-enqury-form-admin-page-icon-btn view"
                              title="View Enquiry Details"
                              onClick={() => handleView(item)}
                            >
                              <FaEye />
                            </button>

                            {item.status !== 'Open' && (
                              <button
                                className="intitutional-enqury-form-admin-page-icon-btn process"
                                title="Process Enquiry (>>)"
                                onClick={() => setProcessingEnquiry(item)}
                              >
                                <FaAngleDoubleRight />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Expand View */}
                      {isRowOpen && (
                        <tr className="intitutional-enqury-form-admin-page-expanded-row">
                          <td colSpan="8">
                            <div className="intitutional-enqury-form-admin-page-expanded-content">
                              
                              {/* 1. Name (< 769px) */}
                              <div className="intitutional-enqury-form-admin-page-exp-item show-exp-lt-768">
                                <span className="intitutional-enqury-form-admin-page-exp-label">Contact Person (Name)</span>
                                <span className="intitutional-enqury-form-admin-page-exp-value">{item.contactPerson || 'N/A'}</span>
                              </div>

                              {/* 2. Status (< 769px) */}
                              <div className="intitutional-enqury-form-admin-page-exp-item show-exp-lt-768">
                                <span className="intitutional-enqury-form-admin-page-exp-label">Status</span>
                                <div>
                                  <span className={`intitutional-enqury-form-admin-page-status-badge ${(item.status || 'open').toLowerCase().replace(/\s+/g, '-')}`}>
                                    {item.status || 'Open'}
                                  </span>
                                </div>
                              </div>

                              {/* 3. Mobile Number (<= 991px) */}
                              <div className="intitutional-enqury-form-admin-page-exp-item show-exp-lt-991">
                                <span className="intitutional-enqury-form-admin-page-exp-label">Mobile Number</span>
                                <span className="intitutional-enqury-form-admin-page-exp-value">{item.mobileNumber || 'N/A'}</span>
                              </div>

                              {/* 4. Official Email (< 1200px) */}
                              <div className="intitutional-enqury-form-admin-page-exp-item show-exp-lt-1200">
                                <span className="intitutional-enqury-form-admin-page-exp-label">Official Email</span>
                                <span className="intitutional-enqury-form-admin-page-exp-value">{item.officialEmail || 'N/A'}</span>
                              </div>

                              {/* 5. Service Required (< 1200px) */}
                              <div className="intitutional-enqury-form-admin-page-exp-item show-exp-lt-1200">
                                <span className="intitutional-enqury-form-admin-page-exp-label">Service Required</span>
                                <span className="intitutional-enqury-form-admin-page-exp-value">{item.serviceRequired || 'N/A'}</span>
                              </div>

                              {/* 6. Designation (Always in expand) */}
                              <div className="intitutional-enqury-form-admin-page-exp-item">
                                <span className="intitutional-enqury-form-admin-page-exp-label">Designation</span>
                                <span className="intitutional-enqury-form-admin-page-exp-value">{item.designation || 'N/A'}</span>
                              </div>

                              {/* 7. WhatsApp (Always in expand) */}
                              <div className="intitutional-enqury-form-admin-page-exp-item">
                                <span className="intitutional-enqury-form-admin-page-exp-label">WhatsApp Number</span>
                                <span className="intitutional-enqury-form-admin-page-exp-value">{item.whatsappNumber || 'N/A'}</span>
                              </div>

                              {/* 8. Location (Always in expand) */}
                              <div className="intitutional-enqury-form-admin-page-exp-item">
                                <span className="intitutional-enqury-form-admin-page-exp-label">Location (City / State)</span>
                                <span className="intitutional-enqury-form-admin-page-exp-value">{item.city || 'N/A'}, {item.state || 'N/A'}</span>
                              </div>

                              {/* 9. Preferred Contact Time (Always in expand) */}
                              <div className="intitutional-enqury-form-admin-page-exp-item">
                                <span className="intitutional-enqury-form-admin-page-exp-label">Preferred Contact Time</span>
                                <span className="intitutional-enqury-form-admin-page-exp-value">{item.preferredContactTime || 'N/A'}</span>
                              </div>

                              {/* 10. Submitted On (Always in expand for all screens) */}
                              <div className="intitutional-enqury-form-admin-page-exp-item">
                                <span className="intitutional-enqury-form-admin-page-exp-label">Submitted On</span>
                                <span className="intitutional-enqury-form-admin-page-exp-value">{formatDate(item.createdAt)}</span>
                              </div>

                              {/* 11. Project Details Scope (Always in expand full width) */}
                              <div className="intitutional-enqury-form-admin-page-exp-item intitutional-enqury-form-admin-page-col-span-full">
                                <span className="intitutional-enqury-form-admin-page-exp-label">Project Details / Scope</span>
                                <div className="intitutional-enqury-form-admin-page-text-content">{item.projectDetails || 'N/A'}</div>
                              </div>

                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredEnquiries.length > 0 && (
          <Pagination
            totalItems={filteredEnquiries.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}

      </div>
    </>
  );
};

export default InstitutionalEnquries;